import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  createContext,
  useContext,
} from "react";
import {
  Clock,
  Search,
  TrendingUp,
  DownloadCloud,
  Upload,
  ChevronDown,
  Plus,
  Bot,
  Loader2,
  MessageSquare,
  CheckCircle,
  RefreshCcw,
  XCircle,
  FileSearch,
  ShieldAlert,
  Layers,
  ArrowDownRight,
  FolderKanban,
  Target,
  Package,
  Calendar,
  Save,
  Trash2,
  Award,
  Sun,
  Moon,
  History as HistoryIcon,
  FolderOpen,
  EyeOff,
  Monitor,
  UserPlus,
  Truck,
  Pencil,
  Info,
  Shield,
  Lock,
  Mail,
  CheckCheck,
  FolderPlus,
  Bell,
  FileCheck,
  Crown,
  LogOut,
  LogIn,
  FileSpreadsheet,
  Users,
  Eye,
  ArrowLeft,
  FileUp,
  Cpu,
  TrendingDown,
  Share2,
  User,
  PackageSearch,
  ArrowRight,
  Building2,
  Loader,
  ClipboardSignature,
  ArrowUp,
  UploadCloud,
  Zap,
  Globe,
  HelpCircle,
  AlertTriangle,
  Database,
  FileDown,
  BarChart3,
  Timer,
  AlertCircle,
  Trophy,
  FileText,
  Edit3,
  Check,
  Wallet,
  Receipt,
  CreditCard,
  Sparkles,
  Medal,
  ArrowUpRight,
  CheckCircle2,
  LayoutGrid,
  List,
  ChevronUp,
  ChevronRight,
  X,
  PieChart as PieIcon,
  ClipboardList,
  ClipboardCheck,
  Download,
  Phone,
  RefreshCw,
  Settings,
  MessageCircle,
  ShieldCheck,
  Rocket,
} from "lucide-react";
import {
  LabelList,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  Legend,
  Label,
  Area,
  AreaChart,
  PieChart,
  ScatterChart,
  Scatter,
  ZAxis,
  Pie,
} from "recharts";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { Toaster, toast } from "sonner";
import { RocketIcon } from "lucide-react";

const FinancePage = ({
  projects,
  user,
  logout,
  setView,
  supList, // ✅ Keep this: It's coming from App.js
  allUsers,
  storage,
  darkMode,
  setProjects,
  generatePDFDocument,
  getOptimalSplitDetails,
}) => {
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [selectedWinners, setSelectedWinners] = useState({});
  const [selectionMode, setSelectionMode] = useState("overall"); // "overall" or "optimal"
  // Change your existing useState line to this:
  const [processedPayments, setProcessedPayments] = useState(() => {
    const savedAudit = localStorage.getItem("quopro_finance_audit");
    return savedAudit ? JSON.parse(savedAudit) : {};
  });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showConfirmInvoiceModal, setShowConfirmInvoiceModal] = useState(false);
  const [isHydrating, setIsHydrating] = useState(false);

  const activeProject = useMemo(
    () => projects?.find((p) => String(p.id) === String(selectedProjectId)),
    [projects, selectedProjectId]
  );

  const highRiskVendors = useMemo(() => {
    // 1. Safety check: Only run if modal is open and project is selected
    if (!showConfirmModal || !selectedProjectId) return [];

    const idStr = String(selectedProjectId);

    // 2. Get the winner names from your LOCAL FinancePage state
    // We check both 'optimal' and 'manual' modes based on selectionMode
    const names = selectedWinners?.[selectionMode]?.[idStr] || [];

    return names
      .map((name) => {
        const searchName = String(name).trim().toLowerCase();

        // 3. Lookup the supplier in the global supList (from App.js)
        const profile = (supList || []).find(
          (s) =>
            String(s.name || "")
              .trim()
              .toLowerCase() === searchName ||
            String(s.supplierName || "")
              .trim()
              .toLowerCase() === searchName
        );

        // 4. Extract Level. If "Level 4", it becomes 4. Fallback to 9 for non-compliant.
        const rawLevel = profile?.beeLevel || "9";
        const levelNumber = parseInt(rawLevel.replace(/\D/g, "")) || 9;

        return {
          name: name,
          level: levelNumber,
          isHighRisk: levelNumber >= 4, // Your threshold for "High Risk"
        };
      })
      .filter((v) => v.isHighRisk);
  }, [
    showConfirmModal,
    selectedProjectId,
    selectionMode,
    selectedWinners,
    supList,
  ]);

  const BACKEND_URL = "https://quopro-backend.onrender.com";

  // Ensure this is at the top of your FinancePage component
  const [isProcessing, setIsProcessing] = useState(false);

  const confirmAndProcess = async () => {
    if (isProcessing) return;

    const mode = selectionMode;
    const getSafeId = (id) =>
      id === null || id === undefined ? null : String(id);
    const currentProjIdStr = getSafeId(selectedProjectId);

    if (!currentProjIdStr)
      return toast.error("Selection Error: No project selected.");

    const selectedNames = selectedWinners[mode]?.[currentProjIdStr] || [];
    const companyId = user?.companyId?.toUpperCase().trim();

    if (!companyId) return toast.error("User workspace ID not found.");

    setIsProcessing(true);

    try {
      const freshData = await storage.get(companyId);
      const cloudProjects = freshData?.projects || [];
      const cloudSuppliers = freshData?.supList || [];
      const masterProject = cloudProjects.find(
        (p) => String(p.id) === currentProjIdStr
      );

      if (!masterProject) {
        setIsProcessing(false);
        return toast.error("Project data not found.");
      }

      // SAFE PARSING HELPER FOR PRICING STRINGS / NUMBERS
      const safeParse = (val) => {
        if (val === undefined || val === null) return 0;
        if (typeof val === 'number') return isNaN(val) ? 0 : val;
        const cleaned = val.toString().replace(/[R\s,]/gi, '');
        const parsed = parseFloat(cleaned);
        return isNaN(parsed) ? 0 : parsed;
      };

      // Compliance Warning Check - Incorporating your exact B-BBEE Level alert triggers
      const highRiskVendors = selectedNames.filter((name) => {
        const profile = cloudSuppliers.find((s) => s.name === name);
        const matches = profile?.beeLevel ? String(profile.beeLevel).match(/\d+/) : null;
        const levelNum = matches ? parseInt(matches[0], 10) : 9;
        return levelNum >= 4; // Flags Levels 4-8 (Orange/Red zones)
      });

      if (highRiskVendors.length > 0) {
        const confirmBypass = window.confirm(
          `Compliance Warning: B-BBEE Level 4+ Suppliers selected. Do you want to proceed with allocation?`
        );
        if (!confirmBypass) {
          setIsProcessing(false);
          return;
        }
      }

      const loadingToast = toast.info(
        "Finalizing Project & Syncing Invoices...",
        { autoClose: false }
      );

      // 1. Update Project Status in Cloud Storage
      const lockedProjects = cloudProjects.map((p) =>
        String(p.id) === currentProjIdStr
          ? { ...p, status: "Completed", lastUpdated: new Date().toISOString() }
          : p
      );

      await storage.set(
        companyId,
        lockedProjects,
        freshData?.users || {},
        (freshData?.activities || []).slice(0, 50),
        cloudSuppliers,
        user.plan || "starter",
        user.email || "",
        darkMode ? "dark" : "light"
      );

      if (typeof setProjects === "function") setProjects(lockedProjects);

      // 2. Process Invoices individually
      const BACKEND_URL = "https://quopro-backend.onrender.com";

      for (const vendorName of selectedNames) {
        const allProjectSups = masterProject.sups || [];
        const supplierSlot = allProjectSups.find(
          (s) => s.name === vendorName || s.supplierName === vendorName
        );

        let vendorProfile =
          cloudSuppliers.find(
            (p) =>
              String(p.id || p.sid) ===
              String(supplierSlot?.sid || supplierSlot?.id)
          ) || cloudSuppliers.find((p) => p.name === vendorName);

        if (!vendorProfile) continue;

        const supplier = vendorProfile || {};

        // 🚩 CRITICAL FEE RESOLUTION: Inspect project slot first, then fall back to profile data
        const deliveryFee = safeParse(supplierSlot?.deliveryFee || supplierSlot?.delivery_fee || supplierSlot?.freight || supplier.deliveryFee || supplier.delivery_fee);
        const insuranceFee = safeParse(supplierSlot?.insuranceFee || supplierSlot?.insurance_fee || supplier.insuranceFee || supplier.insurance_fee);
        const handlingFee = safeParse(supplierSlot?.handlingFee || supplierSlot?.handling_fee || supplier.handlingFee || supplier.handling_fee);
        const miscFee = safeParse(supplierSlot?.miscFee || supplierSlot?.misc_fee || supplier.miscFee || supplier.misc_fee);
        const vatAmount = safeParse(supplierSlot?.vatAmount || supplierSlot?.vat_amount || supplierSlot?.vat || supplier.vatAmount || supplier.vat_amount);

        // 🚩 CRITICAL BULLETPROOF ITEM FILTERING ALGORITHM
        const masterQuoteItems = masterProject.quote || masterProject.items || [];
        let itemsToProcess = [];

        if (mode === "optimal") {
          itemsToProcess = masterQuoteItems.filter((mItem) => {
            const itemRef = String(mItem.ref || "").trim();
            let cheapestSid = null;
            let minPrice = Infinity;

            allProjectSups.forEach((s) => {
              const price = safeParse(s.quotes?.[itemRef]?.price);
              if (price > 0 && price < minPrice) {
                minPrice = price;
                cheapestSid = String(s.sid || s.id);
              }
            });

            const currentSid = String(supplierSlot?.sid || supplierSlot?.id || supplier.sid || supplier.id || "");
            if (currentSid && cheapestSid) {
              return cheapestSid === currentSid;
            }

            let cheapestName = null;
            let minPriceName = Infinity;
            allProjectSups.forEach((s) => {
              const price = safeParse(s.quotes?.[itemRef]?.price);
              if (price > 0 && price < minPriceName) {
                minPriceName = price;
                cheapestName = s.name || s.supplierName;
              }
            });
            return cheapestName === vendorName;
          });
        } else {
          itemsToProcess = masterQuoteItems;
        }

        // Map items safely for Mongoose Schema schema alignment
        let runningItemsSubtotal = 0;
        const formattedItemsForBackend = itemsToProcess.map((item) => {
          const qty = safeParse(item.qty || item.quantity || 1);
          const itemRef = String(item.ref || "").trim();
          const price = mode === "optimal"
            ? safeParse(supplierSlot?.quotes?.[itemRef]?.price || supplier.quotes?.[itemRef]?.price)
            : safeParse(supplierSlot?.quotes?.[itemRef]?.price || item.price || item.unitPrice);

          runningItemsSubtotal += (qty * price);

          return {
            ref: itemRef || "-",
            description: String(item.desc || item.description || "Line Item"),
            quantity: Number(qty),
            price: Number(price),
          };
        });

        // Resolve absolute aggregated landing metrics
        const dynamicGrandTotal = mode === "optimal"
          ? (runningItemsSubtotal + deliveryFee + insuranceFee + handlingFee + miscFee + vatAmount)
          : safeParse(supplierSlot?.fullLandedTotal || supplier.fullLandedTotal || (runningItemsSubtotal + deliveryFee + insuranceFee + handlingFee + miscFee + vatAmount));

        // A. Generate local physical file artifact export download safely
        await generatePDFDocument(
          vendorName,
          masterQuoteItems, // Pass master items to let PDF cleanly apply layout tracking coordinates
          vendorProfile,
          mode === "optimal" ? "FINANCE-PAYMENT-ADVICE" : "FULL-PROJECT-INVOICE",
          masterProject,
          mode
        );

        // B. THE SINGLE DEFINITIVE DATABASE WRITING POINT
        const res = await fetch(`${BACKEND_URL}/api/invoices`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            workspaceId: companyId,
            projectId: currentProjIdStr,
            clientName: vendorName,
            invoiceNumber: `INV-${currentProjIdStr.slice(-3)}-${Math.floor(1000 + Math.random() * 9000)}`,
            items: formattedItemsForBackend,
            deliveryFee: Number(deliveryFee),
            insuranceFee: Number(insuranceFee),
            handlingFee: Number(handlingFee),
            miscFee: Number(miscFee),
            vatAmount: Number(vatAmount),
            itemsSubtotal: Number(runningItemsSubtotal),
            totalAmount: Number(dynamicGrandTotal),
            selectionMode: String(mode),
            status: "Sent",
            currency: "ZAR",
            issueDate: new Date().toISOString(),
            dueDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
            notes: `Project: ${masterProject.name || masterProject.title || currentProjIdStr}`,
          }),
        });

        const responseData = await res.json();
        if (!res.ok || !responseData.success) {
          console.error(
            `❌ DB Save Failed for ${vendorName}:`,
            responseData.message
          );
        } else {
          console.log(
            `✅ DB Save Success for ${vendorName}:`,
            responseData.invoice.invoiceNumber
          );
        }
      }

      toast.dismiss(loadingToast);
      toast.success(
        "Project status: COMPLETED. All records synced to Database."
      );
      setShowConfirmModal(false);
    } catch (err) {
      console.error("FINANCE ERROR:", err);
      toast.error(`Sync Failure: ${err.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  // 1. CALCULATE SUPPLIERS & STRATEGIC TOTALS
  const availableSuppliers = useMemo(() => {
    // 1. Guard Clause: If no project is selected, return nothing
    if (!activeProject) return [];

    // 2. Normalize Project ID to ensure the lookup in processedPayments works
    const currentProjIdStr = String(selectedProjectId);

    const getLandedCostBreakdown = (s) => {
      const q = s?.quotes || {};

      const freight = Number(q.deliveryFee || 0);
      const insurance = Number(q.insuranceFee || 0);
      const handling = Number(q.handlingFee || 0);
      const misc = Number(q.miscFee || 0);
      const vat = Number(q.vatAmount || 0);

      const total =
        Number(q.subtotalAmount || 0) +
        freight +
        insurance +
        handling +
        misc +
        vat;

      return {
        freight,
        insurance,
        handling,
        misc,
        vat,
        total,
      };
    };
    // 3. Get the "Processed/Invoiced" registry for THIS specific project
    // This looks into the state object you updated in handleProcessInvoices
    const projectInvoicedRegistry = processedPayments[currentProjIdStr] || {};

    const masterItems = activeProject.quote || [];
    const allProjectSups = activeProject.sups || [];

    // 4. IDENTIFY OPTIMAL WINNERS (Split Logic)
    // We calculate who is the cheapest for every single item
    const itemWinners = masterItems.map((item) => {
      const itemRef = String(item.ref || "").trim();
      let cheapestSid = null;
      let minPrice = Infinity;

      allProjectSups.forEach((s) => {
        const price = Number(s.quotes?.[itemRef]?.price) || 0;
        if (price > 0 && price < minPrice) {
          minPrice = price;
          cheapestSid = String(s.sid || s.id);
        }
      });

      return {
        ref: itemRef,
        winningSid: cheapestSid,
        price: minPrice,
        qty: Number(item.qty) || 0,
      };
    });

    // 5. MAP SUPPLIERS TO FINAL DISPLAY OBJECTS
    return allProjectSups.map((s) => {
      const currentSid = String(s.sid || s.id);

      // Find the profile in the Supplier Network/Registry
      const profile = supList?.find((p) => String(p.id) === currentSid);

      // The "displayName" MUST match the name used in handleProcessInvoices
      const nameToDisplay = profile?.name || s.name || "Unknown Supplier";

      // Calculate Total for "Full Project" selection
      const fullQuoteTotal = masterItems.reduce((sum, mItem) => {
        const price = Number(s.quotes?.[String(mItem.ref).trim()]?.price) || 0;
        return sum + price * (Number(mItem.qty) || 0);
      }, 0);

      // Calculate Total for "Optimal Split" selection
      const optimalQuoteTotal = itemWinners
        .filter((w) => w.winningSid === currentSid)
        .reduce((sum, wItem) => sum + wItem.price * wItem.qty, 0);


      const fullLandedTotal = Number(s.totalLandedCost ?? 0);

      // 🚩 THE "INVOICED" CHECK
      // We check the registry using the supplier's display name
      const invoiceDate = projectInvoicedRegistry[nameToDisplay] || null;

      return {
        ...s,
        displayName: nameToDisplay,
        fullTotal: fullQuoteTotal,
        fullLandedTotal: fullLandedTotal,
        optimalTotal: optimalQuoteTotal,
        isSplitWinner: optimalQuoteTotal > 0,

        // UI Status Flags
        isInvoiced: !!invoiceDate, // true if a date exists
        invoicedOn: invoiceDate, // the actual date string (e.g., "16 FEB")
      };
    });

    // 🚩 CRITICAL: Dependency array must include processedPayments to trigger UI update
  }, [activeProject, supList, selectedProjectId, processedPayments]);

  const financialStats = useMemo(() => {
    if (!activeProject || availableSuppliers.length === 0) return null;

    const safeNumbers = (arr) =>
      arr.map(Number).filter((n) => Number.isFinite(n) && n > 0);

    const bestSingleQuoteArray = safeNumbers(
      availableSuppliers.map((s) => s.fullTotal)
    );

    const bestSingleQuote =
      bestSingleQuoteArray.length > 0
        ? Math.min(...bestSingleQuoteArray)
        : 0;

    const optimalSplitTotal = availableSuppliers.reduce(
      (sum, s) => sum + (Number(s.optimalTotal) || 0),
      0
    );

    const currentMode = selectionMode;

    const selectedNames =
      selectedWinners?.[currentMode]?.[selectedProjectId] || [];

    const allocatedValue = availableSuppliers
      .filter((s) => selectedNames.includes(s.displayName))
      .reduce((sum, s) => {
        const value =
          currentMode === "optimal"
            ? (
              Number(s.optimalTotal) +
              Number(s.deliveryFee) +
              Number(s.insuranceFee) +
              Number(s.handlingFee) +
              Number(s.miscFee) +
              Number(s.vatAmount)
            ) || 0
            : Number(s.fullLandedTotal) || 0;
        return sum + value;
      }, 0);

    const denominator =
      currentMode === "optimal" ? optimalSplitTotal : bestSingleQuote;

    const progressPercent =
      denominator > 0 ? (allocatedValue / denominator) * 100 : 0;

    const savings = bestSingleQuote - optimalSplitTotal;

    return {
      savings,
      allocatedValue,
      progressPercent: isFinite(progressPercent) ? progressPercent : 0,
      isSaving: savings > 0,
    };
  }, [
    activeProject,
    availableSuppliers,
    selectedWinners,
    selectionMode,
    selectedProjectId,
  ]);

  // Function to auto-select split winners
  // 2. Adjust the toggle function to respect the current selectionMode
  const toggleWinner = (projectId, supplierName) => {
    if (!projectId) return; // Don't run if no project is selected

    setSelectedWinners((prev) => {
      // Ensure the modes exist even if state was reset
      const overall = prev.overall || {};
      const optimal = prev.optimal || {};

      const currentSelection =
        (selectionMode === "overall" ? overall : optimal)[projectId] || [];

      const isSelected = currentSelection.includes(supplierName);
      const newSelection = isSelected
        ? currentSelection.filter((s) => s !== supplierName)
        : [...currentSelection, supplierName];

      return {
        ...prev,
        [selectionMode]: {
          ...(selectionMode === "overall" ? overall : optimal),
          [projectId]: newSelection,
        },
      };
    });
  };

  const [isSyncing, setIsSyncing] = useState(false); // Add this state at the top

  // 3. Update the "selectOptimalWinners" to only touch the optimal bucket
  const selectOptimalWinners = (suppliers) => {
    const optimalIds = suppliers
      .filter((s) => s.optimalTotal > 0)
      .map((s) => s.displayName);

    setSelectedWinners((prev) => ({
      ...prev,
      optimal: {
        ...prev.optimal,
        [selectedProjectId]: optimalIds,
      },
    }));
  };

  useEffect(() => {
    const syncInvoicesFromDB = async () => {
      const activeCompanyId = user?.companyId?.toUpperCase().trim();
      if (!selectedProjectId || !activeCompanyId) return;

      // 🚩 Start Loading
      setIsHydrating(true);

      try {
        const res = await fetch(
          `${BACKEND_URL}/api/invoices/${activeCompanyId}`
        );
        if (!res.ok) throw new Error("Failed to fetch invoices");

        const invoices = await res.json();

        const projectInvoices = invoices.filter(
          (inv) => String(inv.projectId) === String(selectedProjectId)
        );

        const syncBatch = {};
        projectInvoices.forEach((inv) => {
          const date = new Date(inv.issueDate)
            .toLocaleDateString("en-GB", { day: "2-digit", month: "short" })
            .toUpperCase();
          syncBatch[inv.clientName] = date;
        });

        setProcessedPayments((prev) => ({
          ...prev,
          [selectedProjectId]: syncBatch,
        }));
      } catch (err) {
        console.error("Hydration Error:", err);
      } finally {
        // 🚩 Stop Loading (even if it errors)
        // Small delay for UX so it doesn't "flicker" too fast
        setTimeout(() => setIsHydrating(false), 400);
      }
    };

    syncInvoicesFromDB();
  }, [selectedProjectId, user?.companyId]);

  // 1. Check who has already been invoiced for this project
  const currentInvoices = processedPayments[selectedProjectId] || {};
  const invoicedVendorNames = Object.keys(currentInvoices);
  const hasAnyInvoices = invoicedVendorNames.length > 0;

  // 2. Determine WHAT was invoiced
  // If the invoiced name isn't in the 'optimal' list for this project, it was a Single Winner.
  const wasSingleWinnerInvoiced =
    hasAnyInvoices &&
    invoicedVendorNames.some(
      (name) => !selectedWinners["optimal"]?.[selectedProjectId]?.includes(name)
    );
  const wasSplitInvoiced = hasAnyInvoices && !wasSingleWinnerInvoiced;

  // 3. Overall Lock Status
  const isLocked = hasAnyInvoices;

  //MODALS
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout(); // Ensure your logout function is awaited if it's async
    } catch (error) {
      console.error("Logout failed", error);
      setIsLoggingOut(false); // Reset if there's an error
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950 p-4 md:p-6 relative font-sans text-slate-900 dark:text-slate-100 transition-colors duration-500">
      <div className="max-w-7xl mx-auto">
        {/* --- HEADER --- */}
        <div className="max-w-7xl mx-auto">
          {/* Top Navbar */}
          <div className="sticky top-0 z-[100] bg-white/95 dark:bg-slate-950 backdrop-blur-2xl rounded-3xl p-4 mb-6 flex justify-between items-center border border-slate-200/50 dark:border-white/5 shadow-[0_20px_50px_rgba(0,0,0,0.05)] transition-all duration-700">

            {/* 🚩 SUBTLE MESH GLOW */}
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(99,102,241,0.03),transparent_80%)] dark:bg-[radial-gradient(circle_at_50%_50%,rgba(99,102,241,0.08),transparent_80%)] pointer-events-none" />

            {/* 🚩 THE RAZOR EDGE */}
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-indigo-500/10 dark:via-indigo-400/25 to-transparent" />

            {/* CONTENT LAYER */}
            <div className="relative z-[100] flex w-full h-[56px] justify-between items-center">

              {/* LEFT SIDE: LOGO & BRAND */}
              <div className="flex items-center gap-2 group">
                <div className="w-10 h-10 flex items-center justify-center transition-all duration-500 ease-out group-hover:scale-110 group-hover:drop-shadow-[0_15px_30px_rgba(79,70,229,0.5)]">
                  <img
                    src="/logo.png"
                    alt="QuoPro Logo"
                    className="w-full h-full object-contain transition-transform duration-500 ease-out group-hover:rotate-12"
                  />
                </div>

                <button
                  className="block text-left"
                  onClick={() => {
                    setView("dashboard");
                    if (typeof setTab === 'function') setTab("main");
                  }}
                >
                  <h1 className="text-[40px] font-bold md:text-[26px] flex items-center dark:text-white tracking-tighter text-slate-900 transition-all duration-300">
                    Quo
                    <span className="font-bold bg-gradient-to-r from-indigo-600 to-cyan-300 dark:from-indigo-400 dark:to-cyan-400 bg-clip-text text-transparent pr-1 transition-all duration-500 drop-shadow-[0_0_8px_rgba(34,211,238,0.4)]">
                      Pro
                    </span>
                  </h1>
                </button>
              </div>

              {/* RIGHT SIDE: USER ACTIONS */}
              <div className="flex items-center gap-4">
                {/* User Info */}
                <div className="hidden md:flex flex-col items-end px-2 group cursor-default">
                  <span className="text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em]">
                    Logged In As
                  </span>
                  <span className="font-bold text-slate-700 dark:text-slate-200 text-sm tracking-tight transition-colors group-hover:text-indigo-600 dark:group-hover:text-indigo-400">
                    {user?.name}
                  </span>
                </div>

                {/* Receipt/Invoice Button */}
                <button className="p-2.5 text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/30 rounded-xl border border-emerald-100 dark:border-emerald-800/50 hover:bg-emerald-100 dark:hover:bg-emerald-900/50 transition-colors">
                  <Receipt size={20} />
                </button>

                {/* Logout Button */}
                <button
                  onClick={() => setShowLogoutConfirm(true)}
                  className="p-2.5 text-slate-400 hover:text-rose-600 dark:hover:text-rose-400 rounded-xl transition-all group hover:bg-rose-50 dark:hover:bg-rose-900/20"
                >
                  <LogOut
                    size={20}
                    className="group-hover:translate-x-1 transition-transform"
                  />
                </button>
              </div>
            </div>
          </div>

          {/* LOGOUT CONFIRMATION MODAL */}
          {showLogoutConfirm && (
            <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 animate-in fade-in duration-300">
              <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => setShowLogoutConfirm(false)} />
              <div className="relative w-full max-w-sm bg-white dark:bg-slate-900 rounded-[32px] p-8 shadow-2xl border border-slate-200 dark:border-white/10 scale-in-center">

                <div className="w-16 h-16 bg-red-50 dark:bg-red-500/10 rounded-2xl flex items-center justify-center mb-6 mx-auto">
                  <LogOut className="text-red-500" size={32} />
                </div>

                <div className="text-center space-y-2 mb-8">
                  <h3 className="text-xl font-black text-slate-900 dark:text-white uppercase tracking-tight">
                    Confirm Sign Out
                  </h3>
                  <p className="text-sm font-bold text-slate-500 dark:text-slate-400">
                    Are you sure you want to end your current session?
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <button
                    disabled={isLoggingOut}
                    onClick={() => setShowLogoutConfirm(false)}
                    className={`py-4 rounded-2xl font-black text-xs uppercase tracking-widest transition-all bg-slate-100 dark:bg-white/5 
              ${isLoggingOut ? 'opacity-50 cursor-not-allowed' : 'text-slate-400 hover:text-slate-600 dark:hover:text-white'}`}
                  >
                    Cancel
                  </button>

                  <button
                    disabled={isLoggingOut}
                    onClick={handleLogout}
                    className="relative py-4 rounded-2xl font-black text-xs uppercase tracking-widest text-white bg-red-600 hover:bg-red-700 shadow-lg shadow-red-500/30 transition-all active:scale-95 flex items-center justify-center gap-2 overflow-hidden"
                  >
                    {isLoggingOut ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        <span>Signing Out...</span>
                      </>
                    ) : (
                      <span>Sign Out</span>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* --- MAIN WORKSPACE --- */}
        <div className="bg-white dark:bg-slate-950 rounded-[40px] shadow-2xl shadow-slate-400/50 dark:shadow-none border border-slate-100 dark:border-slate-800 flex h-[calc(100vh-180px)] overflow-hidden transition-all">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(99,102,241,0.05),transparent_70%)] dark:bg-[radial-gradient(circle_at_50%_0%,rgba(99,102,241,0.15),transparent_70%)] pointer-events-none" />
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-200 dark:via-white/10 to-transparent" />

          <aside className="w-80 bg-slate-100/60 dark:bg-slate-900/50 border-r border-slate-100 dark:border-slate-800 p-8 flex flex-col">
            <div className="flex items-center gap-3 mb-10 text-emerald-600 dark:text-emerald-400 font-black text-[12px] uppercase tracking-[0.3em]">
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              Invoice Management
            </div>

            <div className="space-y-6">
              {" "}
              {/* Reduced from space-y-8 for a tighter vertical rhythm */}
              {/* Project Selector */}
              <div>
                <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-3 block ml-1">
                  Active Project(s)
                </label>
                <div className="relative">
                  <select
                    value={selectedProjectId}
                    onChange={(e) => {
                      setSelectedProjectId(e.target.value);
                      setSelectedWinners({ overall: {}, optimal: {} });
                    }}
                    className="w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl px-5 py-4 text-sm font-bold text-slate-700 dark:text-slate-200 outline-none focus:ring-4 focus:ring-indigo-500/10 dark:focus:ring-indigo-400/5 shadow-sm transition-all appearance-none cursor-pointer"
                  >
                    <option value="">Choose a project...</option>

                    {/* 🛡️ Alphabetical Sorting Logic */}
                    {[...(projects || [])]
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                  </select>

                  {/* Standard Chevron Icon placeholder if you have one */}
                  <div className="absolute right-5 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                    <ChevronDown size={16} />
                  </div>
                </div>
              </div>
              {/* Button Group and Allocation snapped together */}
              <div className="space-y-3">
                {" "}
                {/* This wrapper ensures buttons and stats stay close */}
                {selectedProjectId && (
                  <div className="p-2 bg-slate-200/30 dark:bg-slate-800/50 rounded-[2rem] flex flex-col gap-1 border border-slate-100 dark:border-slate-700/50">
                    <button
                      onClick={() => setSelectionMode("overall")}
                      className={`flex items-center gap-3 px-5 py-4 rounded-[1.5rem] text-[10px] font-black uppercase tracking-widest transition-all ${selectionMode === "overall"
                        ? "bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-300 shadow-md"
                        : "text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-800"
                        }`}
                    >
                      <Trophy size={16} /> Overall Winner
                    </button>
                    <button
                      onClick={() => {
                        setSelectionMode("optimal");
                        selectOptimalWinners(availableSuppliers);
                      }}
                      className={`flex items-center gap-3 px-5 py-4 rounded-[1.5rem] text-[10px] font-black uppercase tracking-widest transition-all ${selectionMode === "optimal"
                        ? "bg-indigo-600 text-white shadow-xl shadow-indigo-200 dark:shadow-none"
                        : "text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-800"
                        }`}
                    >
                      <Zap size={16} /> Optimal Split
                    </button>
                  </div>
                )}
                {financialStats && (
                  /* REMOVED mt-auto HERE */
                  <div className="animate-in fade-in slide-in-from-left-4 duration-700">
                    <div className="mt-6 bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 p-5 rounded-[2.5rem] shadow-sm border-t-white dark:border-t-slate-700/50">
                      <div className="flex justify-between items-end mb-3">
                        <span className="text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">
                          Allocation
                        </span>
                      </div>
                      <div className="w-full h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-indigo-500 to-indigo-400 rounded-full transition-all duration-1000 ease-out"
                          style={{
                            width: `${financialStats.progressPercent}%`,
                          }}
                        />
                      </div>
                      <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400 mt-3">
                        R{" "}
                        {(financialStats.allocatedValue ?? 0).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}{" "}
                        <span className="text-slate-300 dark:text-slate-600 font-medium">
                          Committed
                        </span>
                      </p>
                      <div className="mt-4">
                        <p className="text-[9px] font-black text-red-400 dark:text-slate-500 uppercase tracking-widest">
                          {selectionMode === "overall" ? "" : "Please Note:"}
                        </p>
                        <div className="mt-2">
                          <p className="text-[9px] text-slate-400 dark:text-slate-500 tracking-widest">
                            {selectionMode === "overall" ? "" : "*The cost reflected as 'Supplier Quote Total' is the base material cost of all quoted items that you save on when splitting the vendors. This excludes the supplier's additional fees. The additional fees and VAT costs are subject to the supplier and their terms and conditions."}
                          </p>
                        </div>
                      </div>
                    </div>

                  </div>
                )}
              </div>
            </div>
          </aside>
          <main className="flex-1 p-12 overflow-y-auto bg-white dark:bg-slate-900 transition-colors">
            {!selectedProjectId ? (
              /* STATE 1: NO PROJECT SELECTED (Ready for Takeoff) */
              <div className="h-full flex flex-col items-center justify-center text-center animate-in fade-in zoom-in duration-700">
                <div className="hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 active:bg-slate-50 hover:dark:bg-slate-800/40 bg-slate-50 dark:bg-slate-800/40 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-[5rem] p-24 max-w-xl">
                  <div className="hover:scale-[1.02] transition-all duration-300 w-24 h-24 bg-white dark:bg-slate-900 rounded-3xl flex items-center justify-center text-indigo-500 dark:text-slate-700 mb-8 mx-auto shadow-inner">
                    <Clock size={48} />
                  </div>
                  <h3 className="text-2xl font-black text-slate-900 dark:text-white uppercase tracking-tight mb-4" style={{
                    filter: `
      /* Layer 1: Core definition (slightly crisper) */
      drop-shadow(0 0 1px rgba(15, 23, 42, 0.45)) 
      /* Layer 2: Mid-range body (darker for depth) */
      drop-shadow(0 0 3px rgba(165, 169, 179, 0.25)) 
      /* Layer 3: Outer soft glow (expanded for 5% boost) */
      drop-shadow(0 0 8px rgba(15, 23, 42, 0.15))`
                  }}>
                    Ready for Takeoff
                  </h3>
                  <p className="text-slate-400 dark:text-slate-500 text-sm leading-relaxed uppercase tracking-widest">
                    <span className="font-bold">Choose a project</span> to begin your
                    financial analysis and procurement.
                  </p>
                </div>
              </div>
            ) : !availableSuppliers || availableSuppliers.length === 0 ? (
              /* STATE 2: PROJECT SELECTED BUT NO QUOTES (Waiting for Quotes) */
              <div className="h-full flex flex-col items-center justify-center text-center animate-in fade-in zoom-in duration-700">
                <div className="bg-slate-50 dark:bg-slate-800/40 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-[5rem] p-24 max-w-xl">
                  <div className="group-hover:-translate-y-0.5 w-24 h-24 bg-white dark:bg-slate-900 rounded-3xl flex items-center justify-center text-amber-500 mb-8 mx-auto">
                    <Clock size={48} className="animate-pulse" />
                  </div>
                  <h3 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight mb-4 uppercase" style={{
                    filter: `
      /* Layer 1: Core definition (slightly crisper) */
      drop-shadow(0 0 1px rgba(15, 23, 42, 0.45)) 
      /* Layer 2: Mid-range body (darker for depth) */
      drop-shadow(0 0 3px rgba(165, 169, 179, 0.25)) 
      /* Layer 3: Outer soft glow (expanded for 5% boost) */
      drop-shadow(0 0 8px rgba(15, 23, 42, 0.15))`
                  }}>
                    Awaiting Data
                  </h3>
                  <p className="text-slate-400 dark:text-slate-500 text-sm leading-relaxed uppercase tracking-widest">
                    Awaiting <span className="font-bold">supplier quotes</span>. Proceed to add supplier quotes to the selected project.
                  </p>
                </div>
              </div>
            ) : (
              /* STATE 3: DATA READY (Show Suppliers) */
              <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                <header className="mb-8 flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h2 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight" style={{
                        filter: `
      /* Layer 1: Core definition (slightly crisper) */
      drop-shadow(0 0 1px rgba(15, 23, 42, 0.45)) 
      /* Layer 2: Mid-range body (darker for depth) */
      drop-shadow(0 0 3px rgba(165, 169, 179, 0.25)) 
      /* Layer 3: Outer soft glow (expanded for 5% boost) */
      drop-shadow(0 0 8px rgba(15, 23, 42, 0.15))`
                      }}>
                        {activeProject?.name}
                      </h2>
                      {isLocked && (
                        <div className="bg-slate-100 dark:bg-slate-800 p-1.5 rounded-lg text-slate-400">
                          <Lock size={14} />
                        </div>
                      )}
                    </div>
                    <p className="text-slate-400 dark:text-slate-500 font-bold uppercase text-[9px] tracking-[0.2em]">
                      {selectionMode === "overall"
                        ? "Single-supplier invoicing review"
                        : `Multi-supplier split: ${selectedWinners[selectionMode]?.[selectedProjectId]
                          ?.length || 0
                        } selected.`}
                    </p>
                  </div>

                  {/* --- EXECUTION BUTTON --- */}
                  {(selectedWinners?.[selectionMode]?.[selectedProjectId]
                    ?.length || 0) > 0 && (
                      <button
                        disabled={isLocked || isHydrating}
                        onClick={() => setShowConfirmModal(true)}
                        className={`flex items-center gap-2 px-7 py-3.5 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all shadow-xl active:scale-95 group ${isLocked
                          ? "bg-slate-100 dark:bg-slate-800/50 text-slate-400 cursor-not-allowed border border-slate-200 dark:border-slate-700"
                          : "bg-slate-900 dark:bg-white dark:text-slate-900 text-white hover:bg-emerald-600 dark:hover:bg-emerald-500 dark:hover:text-white"
                          }`}
                      >
                        {isLocked
                          ? "Project Locked"
                          : `Execute ${selectedWinners[selectionMode][selectedProjectId].length} Invoice(s)`}
                        {!isLocked && (
                          <ArrowUpRight
                            size={16}
                            className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                          />
                        )}
                      </button>
                    )}
                </header>

                {/* --- 🚩 THE PREMIUM LOCKDOWN BANNER (Tightened) --- */}
                {isLocked && !isHydrating && (
                  <div
                    className={`mb-6 p-1 overflow-hidden rounded-[1.5rem] border animate-in zoom-in-95 duration-500 ${wasSingleWinnerInvoiced
                      ? "bg-amber-500/5 border-amber-500/20"
                      : "bg-indigo-500/5 border-indigo-500/20"
                      }`}
                  >
                    <div className="flex flex-col md:flex-row items-center justify-between p-4 gap-4">
                      <div className="flex items-center gap-4">
                        <div
                          className={`p-3 rounded-xl shadow-lg ${wasSingleWinnerInvoiced
                            ? "bg-amber-500 shadow-amber-500/10"
                            : "bg-indigo-500 shadow-indigo-500/10"
                            }`}
                        >
                          <FileCheck size={20} className="text-white" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-0.5">
                            <span
                              className={`w-1.5 h-1.5 rounded-full animate-pulse ${wasSingleWinnerInvoiced
                                ? "bg-amber-500"
                                : "bg-indigo-500"
                                }`}
                            />
                            <p className="text-[9px] font-black uppercase tracking-[0.2em] opacity-60 dark:text-white">
                              Ledger Security
                            </p>
                          </div>
                          <h4 className="text-base font-black text-slate-800 dark:text-slate-200 leading-tight">
                            {wasSingleWinnerInvoiced
                              ? `Fully invoiced to ${invoicedVendorNames[0]}.`
                              : `${invoicedVendorNames.length} Vendor payments finalized.`}
                          </h4>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* --- 🚩 THE SKELETON LOADER GRID (Tightened) --- */}
                {isHydrating ? (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {[1, 2, 3, 4].map((i) => (
                      <div
                        key={i}
                        className="p-6 rounded-[2rem] border-2 border-slate-100 dark:border-slate-800 bg-slate-50/50 animate-pulse flex items-center justify-between"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-2xl bg-slate-200 dark:bg-slate-700" />
                          <div className="space-y-2">
                            <div className="h-4 w-24 bg-slate-200 dark:bg-slate-700 rounded" />
                            <div className="h-2 w-16 bg-slate-100 dark:bg-slate-800 rounded" />
                          </div>
                        </div>
                        <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div
                    className={`grid grid-cols-1 lg:grid-cols-2 gap-4 transition-all duration-700 ${isLocked ? "opacity-80" : ""
                      }`}
                  >
                    {availableSuppliers.map((sup, idx) => {
                      const currentInvoices =
                        processedPayments[selectedProjectId] || {};
                      const invoicedVendorNames = Object.keys(currentInvoices);

                      // 🚩 CHECK IF AN INVOICE EXISTS
                      const hasAnyInvoices = invoicedVendorNames.length > 0;

                      const isInvoiced = invoicedVendorNames.some(
                        (name) =>
                          name.trim().toLowerCase() ===
                          sup.displayName.trim().toLowerCase()
                      );

                      const parseMoney = (value) => {
                        if (value === null || value === undefined) return 0;

                        return (
                          Number(
                            String(value)
                              .replace(/R/g, "")
                              .replace(/,/g, "")
                              .trim()
                          ) || 0
                        );
                      };

                      const quote = sup.quotes || {};

                      const deliveryFee = parseMoney(sup.deliveryFee);
                      const insuranceFee = parseMoney(sup.insuranceFee);
                      const handlingFee = parseMoney(sup.handlingFee);
                      const miscFee = parseMoney(sup.miscFee);
                      const vatAmount = parseMoney(sup.vatAmount);


                      const totalWithFees =
                        parseMoney(sup.optimalTotal) +
                        deliveryFee +
                        insuranceFee +
                        handlingFee +
                        miscFee +
                        vatAmount;

                      // 🚩 THE LOGIC OVERRIDE:
                      // If an invoice exists, ONLY the invoiced person is "Selected".
                      // Otherwise, fall back to the normal state selection.
                      const isSelected = hasAnyInvoices
                        ? isInvoiced
                        : selectedWinners?.[selectionMode]?.[
                          selectedProjectId
                        ]?.includes(sup.displayName) || false;

                      const getLandedCost = (s) => {
                        const q = s.quotes || {};

                        return (
                          parseMoney(s.totalLandedCost ?? s.fullTotal ?? 0) +
                          parseMoney(q.deliveryFee) +
                          parseMoney(q.insuranceFee) +
                          parseMoney(q.handlingFee) +
                          parseMoney(q.miscFee) +
                          parseMoney(q.vatAmount)
                        );
                      };

                      const minLanded = Math.min(
                        ...availableSuppliers.map(getLandedCost)
                      );

                      const isOverallWinner =
                        selectionMode === "overall" &&
                        getLandedCost(sup) === minLanded;

                      const isSplitWinner =
                        selectionMode === "optimal" && sup.isSplitWinner;
                      return (
                        <div
                          key={idx}
                          // Disable clicking if the project is locked or already invoiced
                          onClick={() =>
                            !isLocked &&
                            !hasAnyInvoices &&
                            toggleWinner(selectedProjectId, sup.displayName)
                          }
                          className={`hover:scale-[1.03] hover:shadow-lg group relative flex items-center justify-between p-6 rounded-[2rem] border-2 transition-all duration-500 ${isLocked || hasAnyInvoices
                            ? "cursor-not-allowed"
                            : "cursor-pointer active:scale-[0.98]"
                            } ${isSelected
                              ? "bg-emerald-50/50 dark:bg-emerald-900/10 border-emerald-500 shadow-lg shadow-emerald-500/5"
                              : "bg-white dark:bg-slate-800/40 border-slate-100 dark:border-slate-800 hover:border-indigo-200"
                            }`}
                        >
                          {/* BADGES */}
                          {isInvoiced && (
                            <div className="absolute -top-3 left-6 flex items-center gap-1.5 bg-emerald-600 text-white px-3 py-1 rounded-full shadow-lg z-20">
                              <CheckCircle size={12} strokeWidth={3} />
                              <span className="text-[8px] font-black uppercase tracking-widest">
                                Invoiced
                              </span>
                            </div>
                          )}

                          {/* 🚩 UPDATED: Winner badge only shows if NO invoices exist in this project yet */}
                          {(isOverallWinner || isSplitWinner) &&
                            !hasAnyInvoices && (
                              <div className="absolute -top-3 right-6 flex items-center gap-1.5 bg-amber-500 text-white px-3 py-1 rounded-full shadow-lg z-10 animate-bounce-subtle">
                                <Award size={12} strokeWidth={3} />
                                <span className="text-[8px] font-black uppercase tracking-widest">
                                  Winner
                                </span>
                              </div>
                            )}

                          <div className="flex items-center gap-4">
                            <div
                              className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all duration-500 ${isSelected
                                ? "bg-emerald-500 text-white"
                                : "bg-slate-100 dark:bg-slate-700 text-slate-400"
                                }`}
                            >
                              <Building2 size={22} />
                            </div>
                            <div>
                              <div className="text-right">
                                <h4 className="font-black text-slate-900 dark:text-white text-base leading-none mb-1 capitalize">
                                  {sup.displayName.toLowerCase()}
                                </h4>
                              </div>

                              {/* GETIT! */}
                              <span
                                className={`text-xs font-black tracking-widest ${isSelected
                                  ? "text-emerald-600 dark:text-emerald-400"
                                  : "text-slate-500"
                                  }`}
                              >
                                {selectionMode === "optimal" ? (
                                  <div className="flex flex-col gap-1 text-right">

                                    <span className="text-[11px] text-slate-400 mt-2">
                                      Freight Fee: <span className="font-black">R{deliveryFee.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                    </span>

                                    <span className="text-[11px] text-slate-400">
                                      Insurance Fee: <span className="font-black">R{insuranceFee.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                    </span>

                                    <span className="text-[11px] text-slate-400">
                                      Handling Fee: <span className="font-black">R{handlingFee.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                    </span>

                                    <span className="text-[11px] text-slate-400">
                                      Misc Fee: <span className="font-black">R{miscFee.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                    </span>

                                    <span className="text-[11px] text-slate-400">
                                      VAT Fee: <span className="font-black">R{vatAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                    </span>
                                    <span className="text-[11px] text-slate-400">
                                      *Supplier Quote Total: <span className="font-black">R{sup.optimalTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                    </span>
                                    <span className={`text-[12px] mt-2 text-xs font-black uppercase tracking-widest mt-1 ${isSelected ? "text-emerald-600 dark:text-emerald-400" : "text-slate-500"
                                      }`}>
                                      Optimal Total: R{totalWithFees.toLocaleString(undefined, {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      })}<br />
                                    </span>

                                  </div>
                                ) : (
                                  <div className="ml-12 flex flex-col gap-1 text-right">
                                    <span className="text-[11px] text-slate-400 mt-2">
                                      Freight Fee: <span className="font-black">R{deliveryFee.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                    </span>

                                    <span className="text-[11px] text-slate-400">
                                      Insurance Fee: <span className="font-black">R{insuranceFee.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                    </span>

                                    <span className="text-[11px] text-slate-400">
                                      Handling Fee: <span className="font-black">R{handlingFee.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                    </span>

                                    <span className="text-[11px] text-slate-400">
                                      Misc Fee: <span className="font-black">R{miscFee.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                    </span>

                                    <span className="text-[11px] text-slate-400">
                                      VAT Fee: <span className="font-black">R{vatAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                    </span>
                                    <span
                                      className={`text-[12px] mt-2 text-xs font-black uppercase tracking-widest ${isSelected ? "text-emerald-600 dark:text-emerald-400" : "text-slate-500"
                                        }`}
                                    >
                                      R{sup.fullLandedTotal?.toLocaleString(undefined, {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      })}
                                    </span>
                                  </div>
                                )}
                              </span>

                            </div>

                          </div>

                          <div
                            className={`w-9 h-9 rounded-full border-2 flex items-center justify-center transition-all duration-500 ${isSelected
                              ? "bg-emerald-500 border-emerald-500 text-white shadow-md"
                              : "border-slate-200 dark:border-slate-700 text-transparent"
                              }`}
                          >
                            <Check size={18} strokeWidth={4} />
                          </div>

                        </div>

                      );
                    })}


                  </div>

                )}

              </div>

            )}

          </main>

        </div>

      </div>

      {/* --- CONFIRMATION MODAL --- */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-6 animate-in fade-in zoom-in-95 duration-300">
          <div
            className="absolute inset-0 bg-slate-900/60 dark:bg-slate-950/80 backdrop-blur-xl"
            onClick={() => !isProcessing && setShowConfirmModal(false)}
          />
          <div className="relative bg-white dark:bg-slate-900 rounded-[4rem] shadow-2xl w-full max-w-xl overflow-hidden border border-white/20">
            <div className="p-12 text-center space-y-8">
              {/* ICON SECTION */}
              <div
                className={`w-24 h-24 rounded-full flex items-center justify-center mx-auto ring-8 transition-all duration-500 ${isProcessing
                  ? "bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 ring-indigo-50/50"
                  : highRiskVendors.length > 0
                    ? "bg-amber-50 dark:bg-amber-900/20 text-amber-500 ring-amber-50/50"
                    : "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-500 ring-emerald-50/50"
                  }`}
              >
                {isProcessing ? (
                  <RefreshCw
                    size={48}
                    strokeWidth={3}
                    className="animate-spin"
                  />
                ) : highRiskVendors.length > 0 ? (
                  <ShieldAlert size={48} strokeWidth={3} />
                ) : (
                  <Check size={48} strokeWidth={3} />
                )}
              </div>

              <div className="space-y-3">
                <h3 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight uppercase">
                  {isProcessing
                    ? "Processing..."
                    : highRiskVendors.length > 0
                      ? "Compliance Review"
                      : "Confirm Invoicing"}
                </h3>

                {/* --- B-BBEE RISK WARNING BLOCK --- */}
                {!isProcessing && highRiskVendors.length > 0 && (
                  <div className="bg-rose-50/50 dark:bg-rose-500/5 border-2 border-rose-500/20 rounded-[2rem] p-6 mb-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 bg-rose-500 text-white rounded-xl">
                        <ShieldAlert size={20} />
                      </div>
                      <div>
                        <h4 className="text-sm font-black text-rose-600 dark:text-rose-400 uppercase tracking-tighter">
                          Compliance Warning
                        </h4>
                        <p className="text-[10px] text-rose-500/60 font-bold uppercase tracking-widest">
                          High-Risk B-BBEE Levels Detected
                        </p>
                      </div>
                    </div>

                    <div className="space-y-2">
                      {highRiskVendors.map((vendor) => (
                        <div
                          key={vendor.name}
                          className="flex items-center justify-between bg-white dark:bg-slate-900/50 p-4 rounded-2xl border border-rose-100 dark:border-rose-500/10"
                        >
                          <span className="text-[11px] font-black text-slate-700 dark:text-slate-200 uppercase">
                            {vendor.name}
                          </span>
                          <span
                            className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest ${vendor.level <= 2
                              ? "bg-emerald-500/10 text-emerald-500" // Level 1-2 (Green)
                              : vendor.level <= 5
                                ? "bg-orange-500/10 text-orange-500" // Level 3-5 (Orange)
                                : "bg-rose-500/10 text-rose-500" // Level 6-8 (Red)
                              }`}
                          >
                            Level {vendor.level}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <p className="text-slate-500 dark:text-slate-400 font-bold text-xs uppercase tracking-[0.15em] leading-relaxed px-8">
                  {isProcessing
                    ? "Securing database and generating PDF documents. Please wait..."
                    : `You are about to generate ${selectedWinners[selectionMode][selectedProjectId].length} official payment advice(s).`}
                </p>
              </div>

              <div className="flex gap-4">
                <button
                  disabled={isProcessing}
                  onClick={() => setShowConfirmModal(false)}
                  className="flex-1 py-6 bg-slate-50 dark:bg-slate-800 text-slate-400 rounded-3xl font-black text-[11px] uppercase tracking-widest hover:text-slate-600 transition-all disabled:opacity-30"
                >
                  Go Back
                </button>
                <button
                  disabled={isProcessing}
                  onClick={confirmAndProcess}
                  className={`flex-[2] py-6 rounded-3xl font-black text-[11px] uppercase tracking-widest transition-all shadow-xl flex items-center justify-center gap-3 disabled:bg-slate-400 ${highRiskVendors.length > 0
                    ? "bg-amber-500 hover:bg-amber-600 text-white"
                    : "bg-indigo-600 hover:bg-indigo-700 text-white"
                    }`}
                >
                  {isProcessing ? (
                    <>
                      <RefreshCw
                        size={16}
                        strokeWidth={4}
                        className="animate-spin text-white font-bold"
                      />
                      <span>Processing...</span>
                    </>
                  ) : highRiskVendors.length > 0 ? (
                    "Acknowledge & Execute"
                  ) : (
                    "Execute Now"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(FinancePage); // React.memo prevents unnecessary re-renders
